<template>
  <div>
    <!-- Floating Action Button (przeciągalny dymek; można ukryć) -->
    <button
      v-if="!bubbleHidden"
      ref="fab"
      class="chat-launcher-fab"
      :class="{ 'has-unread': totalUnread > 0, 'is-open': drawer, 'is-dragging': dragging }"
      :style="bubbleStyle"
      @mousedown="startDrag"
      @touchstart="startDrag"
      @keydown.enter.prevent="toggleDrawer"
      @keydown.space.prevent="toggleDrawer"
      aria-label="Otwórz czat"
      :aria-expanded="drawer ? 'true' : 'false'"
      title="Czat (przeciągnij, aby przenieść)"
    >
      <v-icon size="26" color="#fff">{{ drawer ? 'mdi-close' : 'mdi-message-text' }}</v-icon>
      <span v-if="totalUnread > 0 && !drawer" class="chat-launcher-badge">
        {{ totalUnread > 99 ? '99+' : totalUnread }}
      </span>
      <span class="chat-launcher-status-dot" :class="{ online: connected }"></span>
    </button>

    <!-- Drawer with channel list + active conversation -->
    <v-navigation-drawer
      v-model="drawer"
      right
      temporary
      fixed
      :width="drawerWidth"
      class="chat-launcher-drawer"
    >
      <div class="chat-launcher-shell">
        <!-- Header -->
        <div class="chat-launcher-header">
          <div class="chat-launcher-brand-icon"><v-icon size="19" color="#fff">mdi-forum-outline</v-icon></div>
          <div class="chat-launcher-brand-copy">
            <span class="chat-launcher-kicker">BTP Connect</span>
            <span class="chat-launcher-title">Centrum komunikacji</span>
          </div>
          <v-spacer />
          <div class="chat-launcher-live" :class="{ online: connected }">
            <span></span>{{ connected ? 'Na żywo' : 'Łączenie' }}
          </div>
          <v-btn icon small dark class="launcher-header-btn" @click="openFullChat" title="Otwórz pełny widok">
            <v-icon small>mdi-arrow-top-right</v-icon>
          </v-btn>
          <v-btn icon small dark class="launcher-header-btn" @click="drawer = false" title="Minimalizuj do dymka">
            <v-icon small>mdi-window-minimize</v-icon>
          </v-btn>
          <v-btn icon small dark class="launcher-header-btn" @click="closeBubble" title="Zamknij i ukryj dymek">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="chat-launcher-body" :class="{ 'show-thread': mobileThreadOpen }">
          <!-- Left: channels rail -->
          <div class="chat-launcher-rail">
            <div class="rail-head">
              <div>
                <span class="rail-eyebrow">Rozmowy</span>
                <strong>Kanały</strong>
              </div>
              <span v-if="totalUnread" class="rail-total">{{ totalUnread > 99 ? '99+' : totalUnread }}</span>
            </div>
            <label class="rail-search">
              <v-icon size="16">mdi-magnify</v-icon>
              <input v-model="channelSearch" type="search" placeholder="Szukaj kanału…" />
              <button v-if="channelSearch" type="button" aria-label="Wyczyść" @click="channelSearch = ''">
                <v-icon size="14">mdi-close-circle</v-icon>
              </button>
            </label>
            <div v-if="loadingChannels" class="rail-loading">
              <v-progress-circular indeterminate size="20" width="2" color="#7C94FF" />
              <span>Wczytujemy kanały…</span>
            </div>
            <div v-else-if="!filteredChannels.length" class="rail-empty">
              <v-icon size="24">{{ channelSearch ? 'mdi-magnify-close' : 'mdi-forum-remove-outline' }}</v-icon>
              <span>{{ channelSearch ? 'Brak pasujących kanałów' : 'Brak kanałów' }}</span>
            </div>
            <button
              v-for="ch in filteredChannels"
              :key="ch.id"
              type="button"
              class="rail-item"
              :class="{ active: activeChannel && ch.id === activeChannel.id }"
              @click="selectChannel(ch)"
            >
              <span class="rail-item-icon" :style="{ color: ch.color || '#8EA7FF' }">{{ channelIcon(ch) }}</span>
              <div class="rail-item-copy">
                <span class="rail-item-name">{{ ch.name }}</span>
                <span class="rail-item-meta">{{ ch.description || channelTypeLabel(ch) }}</span>
              </div>
              <span v-if="unreadFor(ch.id) > 0" class="rail-item-badge">{{ unreadFor(ch.id) > 99 ? '99+' : unreadFor(ch.id) }}</span>
              <v-icon v-else size="16" class="rail-item-arrow">mdi-chevron-right</v-icon>
            </button>
          </div>

          <!-- Right: messages + input -->
          <div class="chat-launcher-main">
            <div v-if="!activeChannel" class="main-empty">
              <v-icon size="36" color="#9aa3b6">mdi-chat-outline</v-icon>
              <div class="caption mt-2">Wybierz kanał po lewej</div>
            </div>
            <template v-else>
              <div class="main-channel-header">
                <button type="button" class="launcher-back" aria-label="Wróć do kanałów" @click="mobileThreadOpen = false">
                  <v-icon size="20">mdi-arrow-left</v-icon>
                </button>
                <span class="main-channel-icon" :style="{ color: activeChannel.color || '#8EA7FF' }">{{ channelIcon(activeChannel) }}</span>
                <div class="main-channel-copy">
                  <strong>{{ activeChannel.name }}</strong>
                  <span>{{ activeChannel.description || channelTypeLabel(activeChannel) }}</span>
                </div>
                <button type="button" class="main-open-full" title="Pełny widok" aria-label="Otwórz pełny widok" @click="openFullChat">
                  <v-icon size="18">mdi-arrow-expand</v-icon>
                </button>
              </div>

              <div class="main-messages" ref="msgs">
                <div v-if="loadingMessages" class="text-center pa-3">
                  <v-progress-circular indeterminate size="22" width="2" color="#7C94FF" />
                </div>
                <div v-else-if="!messages.length" class="launcher-thread-empty">
                  <span class="launcher-thread-empty-icon"><v-icon size="30">mdi-message-text-fast-outline</v-icon></span>
                  <strong>Rozpocznij rozmowę</strong>
                  <span>Napisz pierwszą wiadomość w tym kanale.</span>
                </div>
                <template v-for="m in messages">
                  <div v-if="m.type === 'system'" :key="m.id" class="msg-system">
                    <span>{{ m.text }}</span>
                  </div>
                  <div v-else :key="m.id" class="msg-row" :class="{ mine: isMine(m) }">
                    <div v-if="!isMine(m)" class="msg-avatar">
                      <img v-if="authorAvatar(m)" :src="authorAvatar(m)" alt="avatar" />
                      <span v-else>{{ authorInitials(m) }}</span>
                    </div>
                    <div class="msg-bubble">
                      <div class="msg-author" v-if="!isMine(m)">{{ authorName(m) }}</div>
                      <div v-if="m.type === 'text' && m.text" class="msg-text">{{ m.text }}</div>
                      <div v-if="m.type === 'img'">
                        <div v-if="m.text && m.text !== m.fileName" class="msg-text mb-1">{{ m.text }}</div>
                        <img
                          v-if="m.fileId && loadedImages[m.fileId]"
                          :src="loadedImages[m.fileId]"
                          :alt="m.fileName || 'Obraz w wiadomości'"
                          class="msg-img"
                          @click="downloadFile(m.fileId, m.fileName)"
                        />
                        <div v-else-if="m.fileId" class="msg-img-placeholder">
                          <v-progress-circular indeterminate size="18" color="#9aa3b6" />
                        </div>
                      </div>
                      <div v-if="m.type === 'file'" class="msg-file" @click="downloadFile(m.fileId, m.fileName)">
                        <v-icon small color="#fff">mdi-file-document-outline</v-icon>
                        <div class="msg-file-info">
                          <div class="msg-file-name">{{ m.fileName }}</div>
                          <div class="msg-file-meta caption">{{ m.fileSize || '' }}</div>
                        </div>
                        <v-icon x-small color="#fff">mdi-download</v-icon>
                      </div>
                      <div class="msg-time">{{ formatTime(m.createdAt) }}</div>
                    </div>
                  </div>
                </template>
              </div>

              <!-- attachments preview -->
              <div v-if="attachments.length" class="attach-row">
                <div v-for="(att, i) in attachments" :key="i" class="attach-chip">
                  <img v-if="att.type === 'img' && att.url" :src="att.url" :alt="att.name || 'Podgląd obrazu'" class="attach-thumb" />
                  <v-icon v-else x-small color="#9aa3b6">mdi-file-document-outline</v-icon>
                  <span class="attach-name">{{ att.name }}</span>
                  <button type="button" class="attach-remove" aria-label="Usuń załącznik" @click="removeAttachment(i)">
                    <v-icon x-small>mdi-close</v-icon>
                  </button>
                </div>
              </div>

              <div class="main-input">
                <v-btn icon class="launcher-attach-btn" @click="$refs.imgInput.click()" title="Dodaj obraz">
                  <v-icon size="19">mdi-image-outline</v-icon>
                </v-btn>
                <v-btn icon class="launcher-attach-btn" @click="$refs.fileInput.click()" title="Dodaj plik">
                  <v-icon size="19">mdi-paperclip</v-icon>
                </v-btn>
                <input type="file" ref="imgInput" multiple accept="image/*" style="display: none;" @change="attachImages" />
                <input type="file" ref="fileInput" multiple accept=".pdf,.docx,.xlsx,.txt,.csv" style="display: none;" @change="attachFiles" />
                <v-textarea
                  v-model="draft"
                  placeholder="Napisz wiadomość…"
                  rows="1"
                  auto-grow
                  hide-details
                  dense
                  outlined
                  dark
                  :disabled="sending"
                  @keydown.enter.exact.prevent="send"
                />
                <button
                  type="button"
                  class="launcher-send"
                  :disabled="(!draft.trim() && !attachments.length) || sending"
                  aria-label="Wyślij wiadomość"
                  @click="send"
                >
                  <v-progress-circular v-if="sending" indeterminate size="18" width="2" color="#fff" />
                  <v-icon v-else size="19" color="#fff">mdi-send</v-icon>
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import axios from 'axios';
import { chatState, markChannelRead } from '@/plugins/chatNotifications';
import { getSocket } from '@/services/chatSocket';
import dayjs from 'dayjs';

export default {
  name: 'ChatLauncher',
  data: () => ({
    drawer: false,
    channels: [],
    activeChannel: null,
    avatarMap: {},
    messages: [],
    draft: '',
    draftsByChannel: {},
    loadingChannels: false,
    loadingMessages: false,
    sending: false,
    currentUserId: null,
    pollTimer: null,
    attachments: [],
    loadedImages: {},
    imageLoadGeneration: 0,
    lastMessageId: null,
    bubbleHidden: false,
    bubblePos: null, // { left, top } w px; null = domyślna pozycja (CSS)
    dragging: false,
    channelSearch: '',
    mobileThreadOpen: false,
    messageRequestId: 0,
  }),
  computed: {
    totalUnread() { return chatState.unreadCount || 0; },
    connected() { return chatState.connected; },
    drawerWidth() {
      return this.$vuetify.breakpoint.smAndDown ? this.$vuetify.breakpoint.width : 600;
    },
    filteredChannels() {
      const query = this.channelSearch.trim().toLocaleLowerCase('pl');
      if (!query) return this.channels;
      return this.channels.filter((channel) => [channel.name, channel.description]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase('pl').includes(query)));
    },
    bubbleStyle() {
      if (!this.bubblePos) return {};
      return {
        left: this.bubblePos.left + 'px',
        top: this.bubblePos.top + 'px',
        right: 'auto',
        bottom: 'auto',
      };
    },
  },
  watch: {
    drawer(open) {
      if (open) {
        if (this.$vuetify.breakpoint.smAndDown) this.mobileThreadOpen = false;
        this.fetchAvatars();
        this.fetchChannels();
        this.pollTimer = setInterval(() => {
          if (document.hidden) return;
          if (this.activeChannel && !this.connected) this.pollNew();
        }, 5000);
      } else {
        if (this.pollTimer) clearInterval(this.pollTimer);
      }
    },
  },
  mounted() {
    this.resolveCurrentUser();
    this.attachSocketListener();
    // Stan dymka (ukryty / pozycja) zapamiętany per użytkownik.
    this.bubbleHidden = localStorage.getItem('btpChatBubbleHidden') === '1';
    try {
      const raw = localStorage.getItem('btpChatBubblePos');
      if (raw) {
        const p = JSON.parse(raw);
        if (p && typeof p.left === 'number' && typeof p.top === 'number') this.bubblePos = p;
      }
    } catch (e) { /* ignore */ }
    this.$nextTick(this.clampBubblePosition);
    window.addEventListener('resize', this.clampBubblePosition);
    // Otwarcie czatu z ikony w górnym pasku.
    this.$root.$on('open-chat', this.openFromExternal);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.socketRetryTimer) clearTimeout(this.socketRetryTimer);
    this.$root.$off('open-chat', this.openFromExternal);
    window.removeEventListener('resize', this.clampBubblePosition);
    this.removeDragListeners();
    const sock = getSocket();
    if (sock && this._onLauncherMessage) sock.off('new_message', this._onLauncherMessage);
    if (sock && this._onLauncherConnect) sock.off('connect', this._onLauncherConnect);
    this.imageLoadGeneration++;
    Object.values(this.loadedImages).forEach((url) => URL.revokeObjectURL(url));
    const draftAttachments = Object.values(this.draftsByChannel)
      .flatMap((draft) => draft.attachments || []);
    [...this.attachments, ...draftAttachments].forEach((att) => { if (att.url) URL.revokeObjectURL(att.url); });
  },
  methods: {
    authHeaders() { return { Authorization: 'Bearer ' + localStorage.getItem('token') }; },
    apiUrl(path) { return process.env.VUE_APP_ROOT_API + path; },
    resolveCurrentUser() {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const rawPayload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const paddedPayload = rawPayload.padEnd(Math.ceil(rawPayload.length / 4) * 4, '=');
        const payload = JSON.parse(atob(paddedPayload));
        this.currentUserId = payload.user?.id || payload.id || payload.sub;
      } catch (e) { /* ignore */ }
    },
    toggleDrawer() { this.drawer = !this.drawer; },
    clampBubblePosition() {
      if (!this.bubblePos) return;
      const size = window.innerWidth <= 600 ? 54 : 58;
      const next = {
        left: Math.max(4, Math.min(window.innerWidth - size - 4, this.bubblePos.left)),
        top: Math.max(4, Math.min(window.innerHeight - size - 4, this.bubblePos.top)),
      };
      this.bubblePos = next;
      try { localStorage.setItem('btpChatBubblePos', JSON.stringify(next)); } catch (e) { /* ignore */ }
    },

    // ── Dymek: przeciąganie + click ──
    startDrag(e) {
      const point = e.touches ? e.touches[0] : e;
      const el = this.$refs.fab;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      this._drag = {
        startX: point.clientX,
        startY: point.clientY,
        origLeft: rect.left,
        origTop: rect.top,
        moved: false,
      };
      window.addEventListener('mousemove', this.onDrag);
      window.addEventListener('mouseup', this.endDrag);
      window.addEventListener('touchmove', this.onDrag, { passive: false });
      window.addEventListener('touchend', this.endDrag);
    },
    onDrag(e) {
      if (!this._drag) return;
      const point = e.touches ? e.touches[0] : e;
      const dx = point.clientX - this._drag.startX;
      const dy = point.clientY - this._drag.startY;
      if (!this._drag.moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        this._drag.moved = true;
        this.dragging = true;
      }
      if (!this._drag.moved) return;
      if (e.cancelable) e.preventDefault();
      const size = 60;
      const left = Math.max(4, Math.min(window.innerWidth - size - 4, this._drag.origLeft + dx));
      const top = Math.max(4, Math.min(window.innerHeight - size - 4, this._drag.origTop + dy));
      this.bubblePos = { left, top };
    },
    endDrag() {
      this.removeDragListeners();
      const wasMove = this._drag && this._drag.moved;
      this._drag = null;
      this.dragging = false;
      if (wasMove) {
        // Zapamiętaj nową pozycję dymka.
        try { localStorage.setItem('btpChatBubblePos', JSON.stringify(this.bubblePos)); } catch (e) { /* ignore */ }
      } else {
        // Bez ruchu = zwykłe kliknięcie → otwórz/zamknij czat.
        this.toggleDrawer();
      }
    },
    removeDragListeners() {
      window.removeEventListener('mousemove', this.onDrag);
      window.removeEventListener('mouseup', this.endDrag);
      window.removeEventListener('touchmove', this.onDrag);
      window.removeEventListener('touchend', this.endDrag);
    },

    // ── Minimalizacja / zamknięcie ──
    closeBubble() {
      this.drawer = false;
      this.bubbleHidden = true;
      localStorage.setItem('btpChatBubbleHidden', '1');
    },
    openFromExternal() {
      // Z górnego paska: przywróć dymek (gdyby był zamknięty) i otwórz okno.
      if (this.bubbleHidden) {
        this.bubbleHidden = false;
        localStorage.setItem('btpChatBubbleHidden', '0');
      }
      this.drawer = true;
    },
    attachSocketListener() {
      // ChatLauncher mountuje się jako dziecko App.vue zanim App.vue.mounted()
      // wywoła initGlobalChat() w chatNotifications. Czyli przy pierwszym
      // mounted() socket jest jeszcze null. Robimy retry co 300ms aż socket
      // się pojawi, potem rejestrujemy listener `new_message` żeby drawer
      // dostawał wiadomości na żywo (bez konieczności zamykania/otwierania).
      const sock = getSocket();
      if (!sock) {
        this.socketRetryTimer = setTimeout(() => this.attachSocketListener(), 300);
        return;
      }
      if (this._listenerAttached) return;
      this._listenerAttached = true;
      this._onLauncherMessage = (msg) => {
        if (this.activeChannel && msg.channelId === this.activeChannel.id) {
          if (!this.messages.find((m) => m.id === msg.id)) {
            this.messages.push(msg);
            this.lastMessageId = msg.id;
            if (msg.type === 'img' && msg.fileId) this.loadImage(msg.fileId);
            this.$nextTick(this.scrollToBottom);
          }
        }
      };
      sock.on('new_message', this._onLauncherMessage);
      // Po (re)connekcie — np. gdy backend wrócił po deployu — ponownie dołącz do
      // pokoju aktywnego kanału, bo serwer wysyła `new_message` tylko do channel_<id>.
      this._onLauncherConnect = () => {
        if (this.activeChannel) sock.emit('join_channel', { channelId: this.activeChannel.id });
      };
      sock.on('connect', this._onLauncherConnect);
      // Kanał mógł być wybrany zanim socket się pojawił — dołącz do jego pokoju.
      if (this.activeChannel) sock.emit('join_channel', { channelId: this.activeChannel.id });
    },
    openFullChat() {
      this.drawer = false;
      this.$router.push('/chat').catch(() => {});
    },
    unreadFor(channelId) { return (chatState.unreadByChannel || {})[channelId] || 0; },
    channelIcon(ch) {
      if (!ch) return '';
      if (ch.icon) return ch.icon;
      if (ch.type === 'private') return '🔒';
      if (ch.type === 'alert') return '🚨';
      if (ch.type === 'archive') return '📦';
      return '#';
    },
    channelTypeLabel(ch) {
      if (!ch) return '';
      if (ch.type === 'private') return 'Kanał prywatny';
      if (ch.type === 'alert') return 'Komunikaty alarmowe';
      if (ch.type === 'archive') return 'Archiwum';
      return 'Kanał firmowy';
    },
    async fetchChannels() {
      this.loadingChannels = true;
      try {
        const res = await axios.get(this.apiUrl('chat/channels'), { headers: this.authHeaders() });
        this.channels = res.data || [];
        if (!this.activeChannel && this.channels.length) {
          this.selectChannel(this.channels[0], false);
        }
      } catch (e) {
        this.channels = [];
      }
      this.loadingChannels = false;
    },
    async selectChannel(ch, openThread = true) {
      if (!ch) return;
      const requestId = ++this.messageRequestId;
      // Dołącz do pokoju kanału na socketcie — bez tego serwer nie wysyła
      // `new_message` do tego klienta (emituje tylko do channel_<id>), więc
      // widget się nie odświeżał mimo statusu „live".
      const sock = getSocket();
      if (this.activeChannel) {
        if (this.draft || this.attachments.length) {
          this.$set(this.draftsByChannel, this.activeChannel.id, {
            text: this.draft,
            attachments: this.attachments,
          });
        } else {
          this.$delete(this.draftsByChannel, this.activeChannel.id);
        }
      }
      this.activeChannel = ch;
      if (sock) sock.emit('join_channel', { channelId: ch.id });
      this.mobileThreadOpen = openThread;
      this.imageLoadGeneration++;
      Object.values(this.loadedImages).forEach((url) => URL.revokeObjectURL(url));
      this.loadedImages = {};
      this.messages = [];
      const savedDraft = this.draftsByChannel[ch.id];
      this.draft = savedDraft?.text || '';
      this.attachments = savedDraft?.attachments || [];
      this.$delete(this.draftsByChannel, ch.id);
      this.lastMessageId = null;
      this.loadingMessages = true;
      try {
        const res = await axios.get(this.apiUrl('chat/messages/' + ch.id), { headers: this.authHeaders() });
        if (requestId !== this.messageRequestId) return;
        this.messages = res.data || [];
        if (this.messages.length) this.lastMessageId = this.messages[this.messages.length - 1].id;
        // Pre-load obrazów z wiadomości
        for (const m of this.messages) {
          if (m.type === 'img' && m.fileId) this.loadImage(m.fileId);
        }
        await markChannelRead(ch.id);
        this.$nextTick(this.scrollToBottom);
      } catch (e) {
        if (requestId !== this.messageRequestId) return;
        this.messages = [];
      } finally {
        if (requestId === this.messageRequestId) this.loadingMessages = false;
      }
    },
    async pollNew() {
      if (!this.activeChannel || !this.lastMessageId) return;
      const channelId = this.activeChannel.id;
      const requestId = this.messageRequestId;
      try {
        const res = await axios.get(
          this.apiUrl('chat/messages/' + channelId + '/new?afterId=' + this.lastMessageId),
          { headers: this.authHeaders() }
        );
        if (!this.activeChannel || this.activeChannel.id !== channelId || requestId !== this.messageRequestId) return;
        const fresh = res.data || [];
        for (const m of fresh) {
          if (!this.messages.find((x) => x.id === m.id)) {
            this.messages.push(m);
            this.lastMessageId = m.id;
            if (m.type === 'img' && m.fileId) this.loadImage(m.fileId);
          }
        }
        if (fresh.length) this.$nextTick(this.scrollToBottom);
      } catch (e) { /* ignore */ }
    },
    async send() {
      const text = this.draft.trim();
      if ((!text && !this.attachments.length) || !this.activeChannel || this.sending) return;
      this.sending = true;
      const channelId = this.activeChannel.id;
      const attachmentsToSend = [...this.attachments];
      const sock = getSocket();
      // 1) Tekst — preferuj WebSocket, fallback REST
      if (text) {
        if (sock && this.connected) {
          sock.emit('send_message', { text, channelId });
        } else {
          try {
            const res = await axios.post(
              this.apiUrl('chat/messages'),
              { text, channelId },
              { headers: this.authHeaders() }
            );
            if (res.data && !this.messages.find((message) => message.id === res.data.id)) {
              this.messages.push(res.data);
              this.lastMessageId = res.data.id;
            }
          } catch (e) { console.error('Failed to send message:', e); }
        }
      }
      // 2) Załączniki — przez REST (POST chat/messages/file z FormData)
      for (const att of attachmentsToSend) {
        try {
          const formData = new FormData();
          formData.append('file', att.file);
          formData.append('channelId', channelId);
          const res = await axios.post(this.apiUrl('chat/messages/file'), formData, { headers: this.authHeaders() });
          // WS broadcast doleci sam, ale gdy offline — wepchnij ręcznie
          if (!this.connected && res.data && !this.messages.find((m) => m.id === res.data.id)) {
            this.messages.push(res.data);
            this.lastMessageId = res.data.id;
            if (res.data.type === 'img' && res.data.fileId) this.loadImage(res.data.fileId);
          }
        } catch (e) { console.error('Failed to upload file:', e); }
      }
      attachmentsToSend.forEach((att) => { if (att.url) URL.revokeObjectURL(att.url); });
      this.$delete(this.draftsByChannel, channelId);
      if (this.activeChannel && this.activeChannel.id === channelId) {
        this.draft = '';
        this.attachments = [];
      }
      this.$nextTick(this.scrollToBottom);
      this.sending = false;
    },
    attachFiles(event) {
      for (const f of Array.from(event.target.files)) {
        this.attachments.push({ type: 'file', file: f, name: f.name });
      }
      event.target.value = '';
    },
    attachImages(event) {
      for (const f of Array.from(event.target.files)) {
        this.attachments.push({ type: 'img', file: f, name: f.name, url: URL.createObjectURL(f) });
      }
      event.target.value = '';
    },
    removeAttachment(index) {
      const [attachment] = this.attachments.splice(index, 1);
      if (attachment && attachment.url) URL.revokeObjectURL(attachment.url);
    },
    async loadImage(fileId) {
      if (!fileId || this.loadedImages[fileId]) return;
      const generation = this.imageLoadGeneration;
      try {
        const res = await axios.post(
          this.apiUrl('aws/download-file/' + fileId),
          {},
          { headers: this.authHeaders(), responseType: 'blob' }
        );
        const url = URL.createObjectURL(res.data);
        if (generation !== this.imageLoadGeneration) {
          URL.revokeObjectURL(url);
          return;
        }
        this.$set(this.loadedImages, fileId, url);
      } catch (e) { /* ignore */ }
    },
    async downloadFile(fileId, fileName) {
      if (!fileId) return;
      try {
        const res = await axios.post(
          this.apiUrl('aws/download-file/' + fileId),
          {},
          { headers: this.authHeaders(), responseType: 'blob' }
        );
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'file';
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) { console.error('Failed to download file:', e); }
    },
    isMine(m) { return m.authorId && this.currentUserId && m.authorId === this.currentUserId; },
    authorName(m) {
      if (m.author) return ((m.author.firstName || '') + ' ' + (m.author.lastName || '')).trim();
      return 'System';
    },
    authorAvatar(m) {
      if (!m || !m.authorId) return null;
      return this.avatarMap[m.authorId] || null;
    },
    authorInitials(m) {
      if (m.author) return ((m.author.firstName || ' ')[0] + ((m.author.lastName || '')[0] || '')).toUpperCase();
      return 'SY';
    },
    async fetchAvatars() {
      try {
        const res = await axios.get(this.apiUrl('chat/avatars'), { headers: this.authHeaders() });
        const map = {};
        (res.data || []).forEach((a) => { if (a && a.avatar) map[a.id] = a.avatar; });
        this.avatarMap = map;
      } catch (e) {
        this.avatarMap = {};
      }
    },
    formatTime(d) { return d ? dayjs(d).format('HH:mm') : ''; },
    scrollToBottom() {
      const el = this.$refs.msgs;
      if (el) el.scrollTop = el.scrollHeight;
    },
  },
};
</script>

<style scoped>
.chat-launcher-fab {
  position: fixed;
  right: 28px;
  bottom: 96px;
  z-index: 1000;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #5478FF 0%, #7B4DFF 60%, #9333EA 100%);
  box-shadow:
    0 10px 28px rgba(84, 120, 255, 0.45),
    0 4px 8px rgba(0, 0, 0, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease;
  outline: none;
}
.chat-launcher-fab::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255,255,255,0.35), rgba(255,255,255,0));
  z-index: -1;
  filter: blur(0.5px);
}
.chat-launcher-fab:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow:
    0 14px 36px rgba(84, 120, 255, 0.6),
    0 6px 12px rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}
.chat-launcher-fab:active {
  transform: translateY(-1px) scale(1.02);
}
.chat-launcher-fab.is-dragging {
  cursor: grabbing;
  transition: none;
  transform: scale(1.08);
  opacity: 0.92;
}
.chat-launcher-fab.is-open {
  background: linear-gradient(135deg, #6E7691 0%, #455A64 100%);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
}
.chat-launcher-fab.has-unread:not(.is-open) {
  animation: chat-pulse 2.2s ease-out infinite;
}
@keyframes chat-pulse {
  0%   { box-shadow: 0 10px 28px rgba(84, 120, 255, 0.45), 0 4px 8px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.25), 0 0 0 0   rgba(244, 67, 54, 0.55); }
  70%  { box-shadow: 0 10px 28px rgba(84, 120, 255, 0.45), 0 4px 8px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.25), 0 0 0 14px rgba(244, 67, 54, 0); }
  100% { box-shadow: 0 10px 28px rgba(84, 120, 255, 0.45), 0 4px 8px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.25), 0 0 0 0   rgba(244, 67, 54, 0); }
}
.chat-launcher-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: linear-gradient(135deg, #F43F5E 0%, #E11D48 100%);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #0F1923;
  box-shadow: 0 2px 6px rgba(244, 63, 94, 0.5);
  letter-spacing: 0.2px;
}
.chat-launcher-status-dot {
  position: absolute;
  bottom: 6px;
  right: 6px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #6E7691;
  border: 2px solid #0F1923;
  transition: background 0.2s ease;
}
.chat-launcher-status-dot.online {
  background: #2DCB73;
  box-shadow: 0 0 6px rgba(45, 203, 115, 0.7);
}
.chat-launcher-drawer {
  z-index: 1100 !important;
}
.chat-launcher-drawer >>> .v-navigation-drawer__content {
  background: #0F1923 !important;
  color: #E6EAF2 !important;
}
.chat-launcher-drawer.v-navigation-drawer {
  background: #0F1923 !important;
}
.chat-launcher-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  color: #E6EAF2;
}
.chat-launcher-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #1A2433;
  border-bottom: 1px solid #2A3142;
  color: #fff;
}
.chat-launcher-title { font-weight: 600; font-size: 15px; }
.chat-launcher-body {
  flex: 1;
  display: flex;
  min-height: 0;
}
.chat-launcher-rail {
  width: 170px;
  border-right: 1px solid #2A3142;
  overflow-y: auto;
  padding: 6px 0;
}
.rail-loading, .rail-empty {
  text-align: center;
  padding: 12px;
  color: #94A0B8;
  font-size: 12px;
}
.rail-item {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  border-left: 3px solid transparent;
}
.rail-item:hover { background: rgba(84, 120, 255, 0.08); }
.rail-item.active { background: rgba(84, 120, 255, 0.16); border-left-color: #5478FF; }
.rail-item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.rail-item-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.chat-launcher-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.main-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6E7691;
}
.main-channel-header {
  padding: 10px 12px;
  border-bottom: 1px solid #2A3142;
  display: flex;
  align-items: center;
  font-size: 13px;
}
.main-messages {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}
.msg-row { margin-bottom: 8px; display: flex; align-items: flex-end; gap: 8px; }
.msg-row.mine { justify-content: flex-end; }
.msg-avatar {
  width: 28px; height: 28px;
  border-radius: 8px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  background: #2C5FF6;
  color: #fff;
  font-size: 11px; font-weight: 700;
}
.msg-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.msg-bubble {
  max-width: 80%;
  background: #1E2D3F;
  padding: 6px 10px;
  border-radius: 10px;
  font-size: 13px;
}
.msg-row.mine .msg-bubble { background: #2C5FF6; color: #fff; }
.msg-author { font-size: 11px; opacity: 0.7; margin-bottom: 2px; }
.msg-text { white-space: pre-wrap; word-break: break-word; }
.msg-file {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  margin-top: 4px;
  background: rgba(0,0,0,0.18);
  border-radius: 6px;
  cursor: pointer;
}
.msg-file:hover { background: rgba(0,0,0,0.28); }
.msg-file-info { flex: 1; min-width: 0; }
.msg-file-name { font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.msg-file-meta { font-size: 10px; opacity: 0.7; }
.msg-img {
  max-width: 100%;
  max-height: 220px;
  border-radius: 6px;
  cursor: pointer;
  display: block;
}
.msg-img-placeholder {
  width: 160px;
  height: 90px;
  background: rgba(0,0,0,0.15);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.msg-time { font-size: 10px; opacity: 0.55; text-align: right; margin-top: 2px; }
.msg-system {
  text-align: center;
  font-size: 11px;
  color: #9aa3b6;
  padding: 4px 0;
  font-style: italic;
}
.attach-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 10px;
  border-top: 1px solid #2A3142;
  background: #131c28;
}
.attach-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 4px;
  background: #1E2D3F;
  border-radius: 6px;
  font-size: 11px;
  max-width: 180px;
}
.attach-thumb {
  width: 24px;
  height: 24px;
  object-fit: cover;
  border-radius: 3px;
}
.attach-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.main-input {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  padding: 8px 10px;
  border-top: 1px solid #2A3142;
}
.main-input >>> .v-textarea textarea {
  color: #E6EAF2 !important;
  max-height: 100px;
  overflow-y: auto;
}
</style>

<style scoped>
/* BTP Conversations — kompaktowy launcher */
.chat-launcher-fab {
  width: 58px;
  height: 58px;
  border-radius: 18px;
  background: linear-gradient(145deg, #6e8aff 0%, #576ff0 58%, #7957df 100%);
  box-shadow: 0 14px 36px rgba(63, 84, 194, 0.38), 0 5px 12px rgba(3, 7, 16, 0.28), inset 0 1px rgba(255, 255, 255, 0.24);
}

.chat-launcher-fab::before { border-radius: 18px; }
.chat-launcher-fab:focus-visible { outline: 2px solid #a9baff; outline-offset: 4px; }
.chat-launcher-fab.is-open { background: linear-gradient(145deg, #3a4257, #282f40); }
.chat-launcher-badge { border-color: #171b27; background: linear-gradient(135deg, #ff7373, #e8496b); }
.chat-launcher-status-dot { border-color: #293145; background: #7e899d; }
.chat-launcher-status-dot.online { background: #42d787; box-shadow: 0 0 0 3px rgba(66, 215, 135, 0.16); }

.chat-launcher-drawer {
  z-index: 1100 !important;
  border-left: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 24px 0 0 24px;
  box-shadow: -30px 0 80px rgba(2, 5, 12, 0.48) !important;
  overflow: hidden;
}

.chat-launcher-drawer >>> .v-navigation-drawer__content,
.chat-launcher-drawer.v-navigation-drawer { background: #171b27 !important; color: #f5f7fc !important; }

.chat-launcher-shell {
  color: #f5f7fc;
  background:
    radial-gradient(circle at 88% -6%, rgba(91, 124, 255, 0.16), transparent 31%),
    #171b27;
}

.chat-launcher-header {
  min-height: 72px;
  padding: 12px 14px 12px 16px;
  gap: 9px;
  background: rgba(31, 37, 53, 0.9);
  border-color: rgba(255, 255, 255, 0.075);
  backdrop-filter: blur(20px);
}

.chat-launcher-brand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 13px;
  background: linear-gradient(145deg, #718cff, #566ff2 60%, #7759df);
  box-shadow: 0 9px 22px rgba(91, 124, 255, 0.28), inset 0 1px rgba(255, 255, 255, 0.2);
}

.chat-launcher-brand-copy { display: flex; flex-direction: column; min-width: 0; }
.chat-launcher-kicker { color: #9cacff; font-size: 8px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; }
.chat-launcher-title { color: #f5f7fc; font-size: 13px; font-weight: 750; white-space: nowrap; }

.chat-launcher-live {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 28px;
  padding: 0 8px;
  color: #9ba5ba;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 9px;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.chat-launcher-live span { width: 6px; height: 6px; border-radius: 50%; background: #778195; }
.chat-launcher-live.online { color: #8be3b4; }
.chat-launcher-live.online span { background: #42d787; box-shadow: 0 0 0 3px rgba(66, 215, 135, 0.11); }

.launcher-header-btn { width: 34px !important; height: 34px !important; border: 1px solid transparent; border-radius: 10px; }
.launcher-header-btn:hover { background: rgba(255, 255, 255, 0.06); border-color: rgba(255, 255, 255, 0.075); }

.chat-launcher-body { background: #171b27; }

.chat-launcher-rail {
  width: 220px;
  flex: 0 0 220px;
  padding: 13px 10px;
  background: rgba(28, 34, 49, 0.78);
  border-color: rgba(255, 255, 255, 0.075);
  scrollbar-color: rgba(142, 167, 255, 0.24) transparent;
}

.rail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 0 5px 9px;
}

.rail-head > div { display: flex; flex-direction: column; }
.rail-eyebrow { color: #8ea7ff; font-size: 8px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; }
.rail-head strong { color: #f5f7fc; font-size: 15px; letter-spacing: -0.01em; }

.rail-total,
.rail-item-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  color: #fff;
  background: linear-gradient(135deg, #ff7373, #e8496b);
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(255, 115, 115, 0.2);
  font-size: 9px;
  font-weight: 800;
}

.rail-search {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 38px;
  margin-bottom: 10px;
  padding: 0 10px;
  color: #8f9aaf;
  background: rgba(10, 14, 24, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 12px;
}

.rail-search:focus-within { border-color: #8ea7ff; box-shadow: 0 0 0 3px rgba(142, 167, 255, 0.1); }
.rail-search input { min-width: 0; flex: 1; color: #f5f7fc; background: transparent; border: 0; outline: 0; font-size: 11px; }
.rail-search input::placeholder { color: #7f8a9e; }
.rail-search button { display: inline-flex; padding: 0; color: #8f9aaf; background: transparent; border: 0; }

.rail-loading,
.rail-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 8px;
  color: #9ba5ba;
  font-size: 11px;
}

.rail-item {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 58px;
  margin-bottom: 6px;
  padding: 7px 8px;
  gap: 9px;
  color: #aeb8ca;
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 14px;
  cursor: pointer;
  transition: background 0.16s ease, border-color 0.16s ease, transform 0.16s ease;
}

.rail-item:hover { background: #292f40; border-color: rgba(255, 255, 255, 0.075); }
.rail-item.active { background: linear-gradient(105deg, rgba(91, 124, 255, 0.2), rgba(167, 139, 250, 0.07)); border-color: rgba(124, 148, 255, 0.23); }

.rail-item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 16px;
  font-weight: 800;
}

.rail-item-copy { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.rail-item-name { overflow: hidden; color: #edf1f8; font-size: 12px; font-weight: 720; text-overflow: ellipsis; white-space: nowrap; }
.rail-item-meta { overflow: hidden; margin-top: 2px; color: #8d98ab; font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.rail-item-arrow { color: #6f7b8f !important; }

.chat-launcher-main { background: transparent; }

.main-empty {
  gap: 4px;
  color: #9ba5ba;
  background: radial-gradient(circle at center, rgba(91, 124, 255, 0.09), transparent 42%);
}

.main-channel-header {
  min-height: 63px;
  padding: 9px 12px;
  gap: 9px;
  background: rgba(31, 37, 53, 0.76);
  border-color: rgba(255, 255, 255, 0.075);
}

.launcher-back {
  display: none;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  color: #dfe5f1;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 12px;
}

.main-channel-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(91, 124, 255, 0.11);
  border: 1px solid rgba(124, 148, 255, 0.16);
  font-weight: 800;
}

.main-channel-copy { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.main-channel-copy strong { overflow: hidden; color: #f5f7fc; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.main-channel-copy span { overflow: hidden; margin-top: 2px; color: #919cb0; font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }

.main-open-full {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  color: #aeb8ca;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 11px;
}

.main-messages {
  padding: 16px 13px;
  scrollbar-color: rgba(142, 167, 255, 0.24) transparent;
}

.launcher-thread-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 100%;
  color: #8f9aaf;
  text-align: center;
  font-size: 11px;
}

.launcher-thread-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  margin-bottom: 6px;
  color: #8ea7ff;
  background: rgba(91, 124, 255, 0.1);
  border: 1px solid rgba(142, 167, 255, 0.16);
  border-radius: 22px;
}

.launcher-thread-empty strong { color: #edf1f8; font-size: 14px; }

.msg-row { gap: 7px; margin-bottom: 9px; }
.msg-avatar { width: 30px; height: 30px; border-radius: 10px; background: linear-gradient(145deg, #718cff, #5c64d9); box-shadow: 0 6px 14px rgba(4, 7, 14, 0.18); }

.msg-bubble {
  max-width: 82%;
  padding: 8px 10px 7px;
  color: #edf1f8;
  background: #1c2231;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 5px 15px 15px 15px;
  box-shadow: 0 7px 18px rgba(4, 7, 14, 0.1);
  font-size: 12px;
  line-height: 1.45;
}

.msg-row.mine .msg-bubble {
  color: #fff;
  background: linear-gradient(145deg, rgba(91, 124, 255, 0.96), rgba(91, 91, 218, 0.94));
  border-color: rgba(255, 255, 255, 0.12);
  border-radius: 15px 5px 15px 15px;
  box-shadow: 0 8px 20px rgba(46, 66, 162, 0.2);
}

.msg-author { color: #b9c3d5; font-size: 9px; font-weight: 700; opacity: 1; }
.msg-text { line-height: 1.48; }
.msg-time { margin-top: 4px; color: #99a4b8; opacity: 0.78; }
.msg-row.mine .msg-time { color: rgba(255, 255, 255, 0.72); }
.msg-system { margin: 7px 0; color: #8f9aaf; font-style: normal; }
.msg-system span { display: inline-block; padding: 4px 9px; background: rgba(28, 34, 49, 0.7); border: 1px solid rgba(255, 255, 255, 0.065); border-radius: 999px; }

.msg-file { border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 10px; }
.msg-img { max-height: 240px; border-radius: 11px; }

.attach-row {
  gap: 7px;
  padding: 8px 10px;
  background: rgba(28, 34, 49, 0.88);
  border-color: rgba(255, 255, 255, 0.075);
}

.attach-chip {
  min-height: 34px;
  padding: 4px 5px;
  color: #b7c0d0;
  background: #252c3e;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
}

.attach-thumb { width: 27px; height: 27px; border-radius: 7px; }
.attach-remove { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; padding: 0; color: #ff8b8b; background: rgba(255, 115, 115, 0.08); border: 0; border-radius: 7px; }

.main-input {
  align-items: center;
  gap: 6px;
  padding: 10px;
  background: rgba(23, 27, 39, 0.96);
  border-color: rgba(255, 255, 255, 0.075);
}

.launcher-attach-btn { flex: 0 0 40px; width: 40px !important; height: 40px !important; color: #aeb8ca !important; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.075); border-radius: 11px; }

.main-input >>> .v-textarea .v-input__slot {
  min-height: 44px !important;
  margin: 0;
  padding: 7px 11px !important;
  background: #222838 !important;
  border: 1px solid rgba(255, 255, 255, 0.075) !important;
  border-radius: 13px !important;
  box-shadow: none !important;
}

.main-input >>> .v-textarea .v-input__slot fieldset { display: none; }
.main-input >>> .v-textarea textarea { color: #f5f7fc !important; font-size: 13px; line-height: 1.45; }
.main-input >>> .v-textarea textarea::placeholder { color: #818ca1; }
.main-input >>> .v-textarea.v-input--is-focused .v-input__slot { border-color: #8ea7ff !important; box-shadow: 0 0 0 3px rgba(142, 167, 255, 0.1) !important; }

.launcher-send {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  padding: 0;
  color: #fff;
  background: linear-gradient(145deg, #6f8bff, #536ff2 58%, #6759de);
  border: 0;
  border-radius: 13px;
  box-shadow: 0 8px 20px rgba(91, 124, 255, 0.27);
}

.launcher-send:disabled { opacity: 0.36; box-shadow: none; }

.rail-item:focus-visible,
.rail-search:focus-within,
.launcher-back:focus-visible,
.main-open-full:focus-visible,
.launcher-send:focus-visible,
.attach-remove:focus-visible { outline: 2px solid #8ea7ff; outline-offset: 2px; }

@media (max-width: 600px) {
  .chat-launcher-drawer { width: 100vw !important; max-width: 100vw !important; border-left: 0; border-radius: 0; }
  .chat-launcher-header { min-height: 68px; padding-top: max(10px, env(safe-area-inset-top)); padding-left: 12px; padding-right: 8px; }
  .chat-launcher-brand-icon { flex-basis: 38px; width: 38px; height: 38px; }
  .chat-launcher-kicker { display: none; }
  .chat-launcher-title { font-size: 12px; }
  .chat-launcher-live { display: none; }
  .launcher-header-btn { width: 32px !important; height: 32px !important; }

  .chat-launcher-rail {
    width: 100%;
    flex: 1 1 100%;
    padding: 14px 12px max(16px, env(safe-area-inset-bottom));
    border-right: 0;
  }

  .rail-item { min-height: 64px; }
  .rail-item-icon { flex-basis: 40px; width: 40px; height: 40px; }
  .rail-item-name { font-size: 13px; }
  .rail-item-meta { font-size: 10px; }

  .chat-launcher-main { display: none; width: 100%; }
  .chat-launcher-body.show-thread .chat-launcher-rail { display: none; }
  .chat-launcher-body.show-thread .chat-launcher-main { display: flex; }
  .launcher-back { display: inline-flex; }
  .main-channel-header { min-height: 64px; padding: 9px 11px; }
  .main-messages { padding: 16px 12px; }
  .msg-bubble { max-width: 86%; }
  .main-input { padding: 9px 9px max(9px, env(safe-area-inset-bottom)); }
  .main-input >>> .v-textarea textarea { font-size: 16px; }

  .chat-launcher-fab { right: 18px; bottom: 86px; width: 54px; height: 54px; border-radius: 17px; }
  .chat-launcher-fab::before { border-radius: 17px; }
}

@media (max-width: 380px) {
  .chat-launcher-title { max-width: 96px; overflow: hidden; text-overflow: ellipsis; }
  .launcher-header-btn:first-of-type { display: none; }
  .launcher-attach-btn { flex-basis: 38px; width: 38px !important; height: 38px !important; }
  .launcher-send { flex-basis: 40px; width: 40px; height: 40px; }
}

@media (prefers-reduced-motion: reduce) {
  .chat-launcher-fab,
  .rail-item { transition: none !important; animation: none !important; }
}
</style>
